import type { ReactNode } from 'react';

import { T } from '../i18n/RichText';
import { getLocale, t } from '../i18n/t';
import { languageLabel, LANGUAGE_NAMES, OTHER_LANGUAGE } from '../profile/languages.ts';
import { CEFR_LEVELS, levelLabel, MAX_LANGUAGE_CHARS, type CefrLevel } from '../profile/profile.ts';
import type { Register } from '../profile/register.ts';
import { Select } from './Select';

/**
 * One labelled control, in daisyUI v5's fieldset idiom.
 *
 * This exists because two screens invented this markup separately and both
 * wrote v4 — see the design's §1. The point is not that the markup is now
 * correct; it is that there is one place left that can be wrong.
 *
 * Three things the call sites kept getting wrong, all verified against the
 * vendored daisyui@5.7.36 rather than recalled:
 *
 * - `.label` is not a field label. Its rule is `white-space: nowrap` and 60%
 *   opacity, inside a `.fieldset` that sets `font-size: .75rem` — so the
 *   longest label on the signup form rendered as an unwrappable 12px caption.
 *   `.fieldset-legend` is the label; `.fieldset-label` is the caption, and it
 *   is the one without the `nowrap` that a hint sentence needs.
 * - `label-text` and `input-bordered` do not exist in v5 at all. They were
 *   removed with v4 and have been inert strings in a `className` since.
 * - `.validator-hint` is revealed by a *general sibling* selector, so it has
 *   to be a sibling of the control rather than wrap it.
 *
 * A `<label htmlFor>` carrying `fieldset-legend` rather than a `<legend>`:
 * `fieldset-legend` is a class, not an element, and a `<legend>` labels the
 * group rather than the control inside it. For a fieldset holding exactly one
 * input, the label association is what a screen reader needs.
 */
type ControlProps = {
  id: string;
  className: string;
  'aria-invalid': true | undefined;
  'aria-describedby': string | undefined;
};

export function Field({
  id,
  label,
  hint,
  error,
  control,
}: {
  id: string;
  label: ReactNode;
  hint?: ReactNode;
  error?: string;
  /**
   * A render prop rather than `children`: the wiring below has to reach the
   * control itself, and `cloneElement` would do it invisibly from the call
   * site's point of view.
   */
  control: (props: ControlProps) => ReactNode;
}) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  // The error first: a screen reader should reach what is wrong before the
  // advice that was already there when it was typed.
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    // `min-w-0`: a fieldset's UA minimum is its content's min-content, so a
    // long option label on the dropdown's trigger widened the page.
    <fieldset className="fieldset min-w-0">
      <label className="fieldset-legend" htmlFor={id}>
        {label}
      </label>
      {control({
        id,
        // `validator` is inert until `aria-invalid` is set: daisyUI's rule is
        // `.validator[aria-invalid]:not([aria-invalid=false]) ~ .validator-hint`.
        // So the ARIA attribute that is correct anyway is also what drives the
        // styling, and no rule has to be restated as an HTML constraint.
        className: 'validator w-full',
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
      })}
      {/* Rendered only when there is an error. `.validator-hint` is
          `visibility: hidden` rather than `display: none`, so an
          always-present one would reserve a blank line under every field. */}
      {error && (
        <p className="validator-hint" id={errorId}>
          {error}
        </p>
      )}
      {hint && (
        <p className="fieldset-label" id={hintId}>
          {hint}
        </p>
      )}
    </fieldset>
  );
}

/**
 * First language: a curated select, with a box for anything not on it.
 *
 * Design §5.2 is why this is constrained at all. The value reaches the system
 * prompt so the tutor can expect an L1's usual mistakes, and a typo degrades
 * that silently — "vietnamse" names a language the model has to guess at, and
 * nothing anywhere reports that it happened.
 */
export function LanguageField({
  choice,
  other,
  error,
  onChoice,
  onOther,
}: {
  choice: string;
  other: string;
  error?: string;
  onChoice: (v: string) => void;
  onOther: (v: string) => void;
}) {
  const choosingOther = choice === OTHER_LANGUAGE;
  return (
    <>
      <Field
        id="nativeLanguage"
        label={<T k="field-language-label" parts={{ muted: (s) => <span className="opacity-60">{s}</span> }} />}
        hint={t('field-language-hint')}
        // Both of `validateProfile`'s language messages are about what was
        // typed in the Other box, so they belong under the box rather than
        // under the select that revealed it. With Other closed there is no
        // box, and the select is the only place left to say it.
        error={choosingOther ? undefined : error}
        control={(props) => (
          <Select
            {...props}
            value={choice}
            onChange={onChoice}
            options={[
              { value: '', label: t('field-language-none') },
              ...LANGUAGE_NAMES.map((name) => ({ value: name, label: languageLabel(name, getLocale()) })),
              { value: OTHER_LANGUAGE, label: t('field-language-other') },
            ]}
          />
        )}
      />
      {choosingOther && (
        <Field
          id="otherLanguage"
          label={t('field-language-other-label')}
          error={error}
          control={(props) => (
            <input
              {...props}
              className={`${props.className} input`}
              value={other}
              onChange={(e) => onOther(e.target.value)}
              // The same bound `validateProfile` checks, so the common case is
              // prevented rather than reported. The check stays, for a value
              // that arrives any other way.
              maxLength={MAX_LANGUAGE_CHARS}
              autoFocus
            />
          )}
        />
      )}
    </>
  );
}

/**
 * English level, as the code plus what it means (design §6), in the register's
 * words (pre-A1 §4).
 *
 * `null` is a stored level this build cannot show. The select is then
 * disabled rather than opened on a guess, because saving a guess is how a
 * stale tab once raised a child to B1.
 */
export function LevelField({
  value,
  register,
  onChange,
}: {
  value: CefrLevel | null;
  register: Register;
  onChange: (v: CefrLevel) => void;
}) {
  return (
    <Field
      id="cefrLevel"
      label={t('field-level-label')}
      hint={value === null ? t('field-level-unknown-hint') : undefined}
      control={(props) => (
        <Select
          {...props}
          value={value}
          disabled={value === null}
          placeholder={t('field-level-unknown')}
          onChange={(v) => onChange(v as CefrLevel)}
          options={CEFR_LEVELS.map((level) => ({ value: level, label: levelLabel(level, register) }))}
        />
      )}
    />
  );
}
